import * as THREE from 'three';
import gsap from 'gsap';
import { Howler } from 'howler';
import { Time } from '@/core/Time';
import { Sizes } from '@/core/Sizes';
import { SceneManager } from '@/scene/SceneManager';
import { Renderer } from '@/renderer/Renderer';
import { Camera } from '@/camera/Camera';
import { CameraController } from '@/camera/CameraController';
import { MouseLook } from '@/camera/MouseLook';
import { LoaderFactory } from '@/loader/LoaderFactory';
import { AssetManager } from '@/loader/AssetManager';
import { MaterialManager } from '@/materials/MaterialManager';
import { World } from '@/world/World';
import { ScrollManager } from '@/scroll/ScrollManager';
import { ScrollCameraTest } from '@/animation/ScrollCameraTest';
import { AnimationManager } from '@/animation/AnimationManager';
import { InteractionManager } from '@/interaction/InteractionManager';
import { PerformanceMonitor } from '@/performance/PerformanceMonitor';
import { PostProcessing } from '@/postprocessing/PostProcessing';
import { SelectiveBloom } from '@/postprocessing/SelectiveBloom';
import { AudioManager } from '@/audio/AudioManager';
import { StoneAudio } from '@/audio/StoneAudio';
import { SceneAudio } from '@/audio/SceneAudio';
import { OrbitControlsManager } from '@/controls/OrbitControlsManager';
import { StoneInteraction } from '@/interaction/StoneInteraction';
import { CursorGlow } from '@/interaction/CursorGlow';
import { StoryMode } from '@/ui/StoryMode';
import { Physics } from '@/physics/Physics';
import { SceneTransition } from '@/world/SceneTransition';
import { VideoTransition } from '@/ui/VideoTransition';
import { IntroSequence } from '@/animation/IntroSequence';
import { RevealEffect } from '@/world/RevealEffect';
import { Preloader } from '@/ui/Preloader';
import { UIManager } from '@/ui/UIManager';
import { ScrollReadout } from '@/ui/ScrollReadout';
import { CinematicOverlay } from '@/ui/CinematicOverlay';
import {
  focalAtFrame,
  resolveSection,
  SECTIONS,
  TOTAL_FRAMES,
  MAGNETIC_POINTS,
  type MagneticPoint,
} from '@/animation/SceneSections';
import { Capabilities } from '@/core/Capabilities';
import { assets as assetManifest, isDebug, settings } from '@/config';
import { createLogger } from '@/utils/logger';
import type { GPUCapabilities } from '@/core/Capabilities';
import type { Debug } from '@/debug/Debug';
import type { SizesState, TickState } from '@/types';

export interface ExperienceOptions {
  canvas: HTMLCanvasElement;
}

/** Extra scroll-input-locked settle time after a section transition's own fade
 *  clears, so the camera keeps decelerating into place a little longer before the
 *  user can scroll again (2026-07-05, user request). */
const SECTION_SETTLE_MS = 400;

/**
 * Root orchestrator. Constructs and wires every subsystem, owns the single
 * update/resize order, and exposes a clean lifecycle (init / start / stop /
 * dispose). Subsystems never reach into each other — they are composed here.
 */
export class Experience {
  readonly canvas: HTMLCanvasElement;
  readonly sizes: Sizes;
  readonly time: Time;
  readonly sceneManager: SceneManager;
  readonly renderer: Renderer;
  readonly camera: Camera;
  readonly cameraController: CameraController;
  readonly mouseLook: MouseLook;
  readonly loaders: LoaderFactory;
  readonly assets: AssetManager;
  readonly materials: MaterialManager;
  readonly world: World;
  readonly scroll: ScrollManager;
  readonly scrollCamera: ScrollCameraTest;
  readonly animation: AnimationManager;
  readonly interaction: InteractionManager;
  readonly performance: PerformanceMonitor;
  readonly post: PostProcessing;
  /** Additive glow on the emissive stones + orb + portal, without the full composer. */
  readonly selectiveBloom: SelectiveBloom;
  readonly audio: AudioManager;
  /** Procedural + sample stone-interaction audio engine (5 families). See {@link StoneAudio}. */
  readonly stoneAudio = new StoneAudio();
  /** Per-chapter music beds + spatial environmental loops + transition swell. See {@link SceneAudio}. */
  readonly sceneAudio = new SceneAudio();
  readonly orbit: OrbitControlsManager;
  stoneInteraction?: StoneInteraction;
  /** The cursor-follow glow plane riding the stone hover ray. See {@link CursorGlow}. */
  cursorGlow?: CursorGlow;
  /** Per-chapter "Know More" story: ring cursor, shatter, dark grade, narrated subtitles.
   *  See {@link StoryMode}. */
  storyMode?: StoryMode;
  /** Mirrors the About panel's open state — several places must NOT hand scroll back while it
   *  is up (story close, section transitions). */
  private aboutOpen = false;
  physics?: Physics;
  sceneTransition?: SceneTransition;
  /** The user's smoke-burst clip for the BOOKENDS (post-preloader + restart loop) —
   *  checkpoints keep the spiral shader. See {@link VideoTransition}. */
  videoTransition?: VideoTransition;
  /** Opening camera fly-in (0 → intro.endFrame), then hands control to scroll. */
  introSequence?: IntroSequence;
  /** Shader dissolve that reveals model scene 1 across the 0→40 intro fly-in. */
  reveal?: RevealEffect;
  /** The cinematic build-up timeline (clouds/stones/water) — see {@link playCinematicIntro}. */
  private cinematicTl: gsap.core.Timeline | null = null;
  /** Boot preloader overlay (progress + fade-out). */
  readonly preloader = new Preloader();
  /** Front-end overlay UI (fades in after the intro fly-in). */
  readonly ui = new UIManager();
  /** Film grain + vignette DOM overlay ("in front of the camera"). See {@link CinematicOverlay}. */
  readonly cinematic = new CinematicOverlay();
  /** Left-side timeline HUD — reads out the CLIP FRAME so section/animation boundaries can be
   *  pinned down exactly (`settings.debug.scrollReadout`). See {@link ScrollReadout}. */
  readonly scrollReadout = settings.debug.scrollReadout ? new ScrollReadout() : null;
  debug?: Debug;
  /** Detected GPU/WebGPU capabilities (drives the render backend + quality defaults). */
  caps?: GPUCapabilities;
  /** Current scroll section's name — read by the debug readout. */
  readonly sectionState = { name: SECTIONS[0]!.name };

  private readonly log = createLogger('experience');
  private offTick: (() => void) | null = null;
  private offResize: (() => void) | null = null;
  private started = false;
  /** Section 1 (index 0) is the default World already builds into — start here so the
   *  first tick doesn't immediately "cross a boundary" into itself. */
  private currentSectionIndex = 0;

  /** The chapter the viewer is currently in (0-based) — read by the debug panel. */
  get currentChapterIndex(): number {
    return this.currentSectionIndex;
  }

  private sectionTransitioning = false;
  /** Scroll position captured when a section transition locks input — re-pinned every tick for
   *  the same reason magnets pin (see {@link pinScrollTo}): `lenis.stop()` can't block
   *  non-cancelable momentum wheel events, which scroll the page natively mid-flash. */
  private sectionLockScroll = 0;

  /** Magnetic-scroll state. When `active`, scroll is locked and the camera auto-glides from
   *  `fromFrac`→`toFrac` (clip fractions) over `settings.scenes.magnetDurationSec`. `armed[i]`
   *  tracks each `MAGNETIC_POINTS[i]` so a point fires once until the user scrolls back past it. */
  private readonly magnet = {
    active: false,
    point: null as MagneticPoint | null,
    elapsed: 0,
    fromFrac: 0,
    toFrac: 0,
    arrived: false,
    /** Has the destination focal length been handed to the camera yet? (Gated on the point's
     *  `focalFrame`, so e.g. C1→C2's 35→20mm eases across frames 60→64, not the whole glide.) */
    fovApplied: false,
    /** The FOCAL LENGTH (mm) captured at the grab — the continuous zoom interpolates from here
     *  in log-focal space. `null` = no continuous zoom this glide (checkpoint jumps snap fov
     *  under cloud cover). */
    zoomFromFocal: null as number | null,
    /** Span-scaled duration for THIS glide (the restart outro — see startMagnet); `null` = use
     *  the point's / the default duration. */
    durOverride: null as number | null,
    /** The user's scroll velocity (clip-fraction/sec) at the instant of the grab — the glide's
     *  initial slope is matched to it so the pull is C¹-continuous with the user's own motion:
     *  no perceptible hitch at the hand-over (2026-07-22, user: "I don't want the user to even
     *  know they're getting pulled"). */
    v0: 0,
  };
  /** Clip-fraction velocity estimate, updated every tick — feeds `magnet.v0` at a grab. */
  private prevClipFraction: number | null = null;
  private clipVel = 0;
  /** The CAMERA's clip fraction last tick — magnet trigger detection keys off this, not raw
   *  scroll (2026-09-01: a hard flick puts raw scroll past the restart trigger instantly,
   *  which grabbed the restart magnet while the viewer was still watching chapter 1 — content
   *  and rail then froze for the whole silent outro; the user's "when I scroll fast the
   *  content doesn't change" bug). */
  private prevCamFraction: number | null = null;

  /** STORY IMPULSE (2026-09-02; shake + glitch removed same day, user: "remove that shaking
   *  effect and glitch effect, just keep the camera thing"). `storyRecoil` 0..1 eases the
   *  camera's step back (× `settings.storyMode.recoil.distance`, via `CameraController.dolly`). */
  private readonly storyRecoil = { value: 0 };
  private recoilTween: gsap.core.Tween | null = null;
  /** VOICE PULSE (2026-09-02 balance pass, user: "user more focus on subtitles"): eased toward
   *  1 while the narrator speaks, back to the idle ember level in her gaps — the story world's
   *  orange energy breathes with her voice, pulling the eye to the words. */
  private voicePulse = settings.storyMode.grade.pulse.idle;
  private voicePulseTarget = settings.storyMode.grade.pulse.idle;
  private readonly magnetArmed = MAGNETIC_POINTS.map(() => true);
  /** Arm state for the REVERSE magnet zones (backward crossings of each point's destination
   *  frame) — re-armed while the user sits past the destination. See `checkMagnetTrigger`. */
  private readonly reverseArmed = MAGNETIC_POINTS.map(() => true);
  /** Pending smooth text swap for NON-magnet boundary crossings (reverse scrolling) — the old
   *  text fades out, then this brings the new chapter's text in after a short beat. */
  private sectionTextCall: gsap.core.Tween | null = null;

  constructor(options: ExperienceOptions) {
    this.canvas = options.canvas;

    this.sizes = new Sizes();
    this.time = new Time();
    const sizeState = this.sizes.state;

    this.renderer = new Renderer(this.canvas, sizeState);
    this.sceneManager = new SceneManager();
    this.camera = new Camera(sizeState);
    this.cameraController = new CameraController(this.camera.instance);
    this.mouseLook = new MouseLook(this.cameraController, this.canvas);

    this.loaders = new LoaderFactory(this.renderer.instance);
    this.assets = new AssetManager(this.loaders);
    this.materials = new MaterialManager(this.renderer);
    this.world = new World(this.sceneManager, this.materials, this.renderer);

    this.scroll = new ScrollManager();
    this.scrollCamera = new ScrollCameraTest(this.cameraController, this.scroll);
    this.animation = new AnimationManager();
    this.interaction = new InteractionManager(this.camera.instance, this.canvas);
    this.performance = new PerformanceMonitor(this.renderer);
    this.post = new PostProcessing(
      this.renderer,
      this.sceneManager.scene,
      this.camera.instance,
      sizeState,
    );
    this.selectiveBloom = new SelectiveBloom(
      this.renderer,
      this.sceneManager.scene,
      this.camera.instance,
      sizeState,
    );
    this.audio = new AudioManager();
    this.orbit = new OrbitControlsManager(this.camera.instance, this.canvas);

    this.offResize = this.sizes.on('resize', this.onResize);
    this.offTick = this.time.on('tick', this.onTick);
  }

  /** Asynchronously load assets, build the world, warm up, then start the loop. */
  async init(): Promise<void> {
    this.log.info('initializing ORVAE technical foundation…');

    // SCROLL STAYS LOCKED UNTIL THE VIEWER ENTERS (2026-09-01, found while reproducing the
    // fast-scroll bug): the wheel was live behind the preloader, so the journey could be
    // scrolled deep into the timeline before ENTER was ever clicked — the intro then fought a
    // scroll position chapters away. Unlocked by the intro fly-in's completion (autoplay) or
    // explicitly in the preloader-finish handler (non-autoplay path).
    this.scroll.lockInput();

    // The end-of-scroll loop snap must wait for the CAMERA to arrive too — raw scroll can now
    // reach the end seconds ahead of it (see `ScrollManager.loopGate`).
    this.scroll.loopGate = () => this.scrollCamera.lastSampledFraction >= 0.995;

    // Probe the device's GPU (WebGPU adapter + WebGL2) before building the world so
    // backend-aware systems (e.g. the cloud sea) can pick quality defaults.
    this.caps = await Capabilities.detect();
    // Weak / software-WebGL ("CPU") devices: pick the whole quality profile UP FRONT, before the
    // world is built, so the expensive counts (cloud puffs = draw calls, grass blades) are applied
    // at build time rather than re-scattered later. The FPS monitor can still shift tier at runtime
    // (live-safe levers only) via `applyQualityProfile`.
    // Apply the DETECTED tier's boot-only counts (see `Capabilities.classifyTier`). These are the
    // levers that can't be changed later without a hitch — cloud puffs are sprite instances and
    // grass blades need a full re-scatter — so they're locked in before the world is built.
    // `high` deliberately changes nothing: its profile IS the authored scene.
    if (this.caps.tier !== 'high') {
      const p = settings.performance.profiles[this.caps.tier];
      settings.cloudSea.count = Math.min(settings.cloudSea.count, p.puffs);
      settings.grass.maxBladesTotal = Math.min(settings.grass.maxBladesTotal, p.grassBlades);
      settings.cloudSea.soft.enabled = p.softParticles;
      settings.selectiveBloom.resolutionScale = p.bloomScale;
      this.log.info(`device tier "${this.caps.tier}" — applying that quality profile at boot.`);
    } else {
      this.log.info('device tier "high" — serving the full-quality scene, no reductions.');
    }

    // GPU GATE for the glow. The bloom pass costs an extra scene traversal + a blur mip chain
    // every frame — too much for a software/CPU renderer. Without a real GPU we drop the pass
    // entirely and let the emissive MATERIALS carry the look instead (blown-out white portal core
    // + additive halo + additive stone glow) — a subtle glow for zero extra passes.
    const bl = settings.selectiveBloom;
    const hasGpu = this.caps.hardwareAccelerated && !this.caps.lowPower;
    if (bl.requireGpu && !hasGpu) {
      bl.enabled = false;
      settings.portalLight.halo.intensity *= bl.fallbackGlow;
      settings.stones.emission.intensity *= bl.fallbackGlow;
      this.log.info('no hardware GPU — bloom pass OFF; emissive materials carry the glow instead.');
    }

    this.assets.on('progress', ({ fraction, url }) => {
      this.preloader.setProgress(fraction);
      this.log.debug(`loading ${(fraction * 100).toFixed(0)}% — ${url}`);
    });

    try {
      await this.assets.loadAll(assetManifest);
    } catch (error) {
      this.log.error('asset loading failed', error);
    }

    this.world.build(this.assets, {
      interaction: this.interaction,
      scrollCamera: this.scrollCamera,
      cameraController: this.cameraController,
      camera: this.camera,
    });

    if (settings.stones.enabled && this.world.interactiveStones.length) {
      this.stoneInteraction = new StoneInteraction(this.camera.instance, this.canvas);
      this.stoneInteraction.setStones(this.world.interactiveStones);
      if (settings.stones.physics.enabled) {
        try {
          this.physics = await Physics.create();
          this.stoneInteraction.setPhysics(
            this.physics,
            this.world.structureColliders,
            this.world.floorY,
          );
        } catch (error) {
          this.log.warn('Rapier physics unavailable — falling back to the spring wave.', error);
        }
      }
      this.stoneInteraction.setEmission(this.world.emissiveStones, this.sceneManager.scene);

      // The subtle cursor-follow glow plane (independent of the emission system).
      this.cursorGlow = new CursorGlow(this.stoneInteraction, this.camera.instance);
      this.sceneManager.add(this.cursorGlow.mesh);
      // Keep the glow quad OUT of the cloud depth prepass (it would stamp a solid rectangle
      // into the depth the clouds fade against) — only the glow itself may be visible.
      this.world.depthPrepassExclude.push(this.cursorGlow.mesh);
      this.setupStoneAudio();
    }
    this.setupAmbient();
    this.setupSceneAudioPositions();
    this.setupUiHandlers();
    // Only the emissive objects go on the bloom layer — nothing else is drawn into the bloom
    // source, so the sky gradient and every texture are untouched by definition.
    this.world.markBloomObjects();
    // …and hide the heavy subtrees during that pass so it doesn't walk hundreds of cloud sprites.
    // The cursor glow also skips the bloom pass entirely — it is an overlay, not an emitter,
    // and the darken pass would otherwise swap its additive material for an opaque stand-in.
    // The user's smoke-burst clip for the two bookends — a WebGL quad (created before the
    // bloom-skip list below so it can be excluded like every other overlay).
    if (settings.videoTransition.enabled) {
      this.videoTransition = new VideoTransition();
      this.sceneManager.add(this.videoTransition.group);
      this.world.depthPrepassExclude.push(this.videoTransition.group);
    }

    // STORY MODE (2026-08-18) — the per-chapter "Know More" hold. Built here because it needs
    // the canvas (to snapshot a frame for the shatter) and the world's material set (for the
    // cinematic dark grade). See `settings.storyMode`.
    if (settings.storyMode.enabled) {
      this.storyMode = new StoryMode({
        canvas: this.canvas,
        grade: this.world.storyGrade,
        setAtmosphere: (k) => this.world.setStoryAtmosphere(k),
        onOpen: () => {
          // Freeze the JOURNEY, not the world: scroll locks, but the stones stay LIVE
          // (2026-09-01, user: "even in shader mode I can access that stones interactivity —
          // same emission, but the stones now have the shader mode effect on"). The hover wave,
          // the emissive twins and the cursor-follow glow circle all keep working inside the
          // dark grade — the additive glow materials are deliberately never graded (see
          // `World.applyStoryGrade`), so the emission punches through the smoke.
          // (Hiding the chapter UI is `StoryMode`'s own job — see its `enter`.)
          this.scroll.lockInput();
          this.applyStoryImpulse(true);
          // The chapter-3 rainbow lens ring is a DAYLIGHT artefact — it fades out with the
          // smoke (2026-09-02, user: "when shader mode is on I don't want that rainbow").
          this.cinematic.setRainbowMuted(true);
        },
        onClose: () => {
          // Only hand scroll back if nothing ELSE is holding it (a magnet glide, a section
          // transition or the About panel can all be mid-flight when a story closes).
          if (!this.magnet.active && !this.sectionTransitioning && !this.aboutOpen) {
            this.scroll.unlockInput();
          }
          if (this.stoneInteraction) this.stoneInteraction.enabled = settings.stones.enabled;
          this.applyStoryImpulse(false);
          // The rainbow returns with the daylight (the chapter gate still decides whether
          // this chapter shows it at all).
          this.cinematic.setRainbowMuted(false);
        },
        // Duck the music under the narrator so her voice sits on top, then lift it back — and
        // set the world's VOICE PULSE target: the ember energy swells while she speaks.
        onSpeakingChange: (speaking) => {
          const v = settings.storyMode.voice;
          Howler.volume(speaking ? v.duckMusicTo : 1);
          const p = settings.storyMode.grade.pulse;
          this.voicePulseTarget = speaking ? p.speaking : p.idle;
        },
        // Leaving gets the same quiet confirmation entering has (2026-09-01, user: "same sound
        // effect on close") — the About panel's close SFX, already loaded, in the same family.
        onCloseSound: () => {
          if (this.audio.enabled) this.audio.play('ui-about-close');
        },
      });
    }

    // Second story-grade pass: `StoneInteraction` above builds a material per emissive arch twin,
    // so those only exist now. Re-running catches them (the patch is idempotent).
    this.world.applyStoryGrade();

    const bloomSkip = [...this.world.bloomTraversalSkip()];
    if (this.cursorGlow) bloomSkip.push(this.cursorGlow.mesh);
    // The camera-fog envelope and the video-transition quad are overlays, not emitters —
    // hidden during the bloom pass so their full-screen surfaces are never swapped for opaque
    // black stand-ins (which would black out the glow exactly when they cover the lens).
    if (this.world.cameraFog) bloomSkip.push(this.world.cameraFog.group);
    if (this.videoTransition) bloomSkip.push(this.videoTransition.group);
    this.selectiveBloom.setTraversalSkip(bloomSkip);

    // Adaptive quality also scales the water reflection RT + drops the fluid sim, and
    // MUST resize the postprocessing composer to match the renderer's new effective
    // pixel ratio (`Renderer.setPixelRatioScale` alone left the composer's own
    // WebGLRenderTarget stale — see `PostProcessing.resize`'s comment).
    this.performance.onQualityChange = (tier) => this.applyQualityProfile(tier);

    // Start on the tier the device actually earned, applying the LIVE levers too (render scale,
    // grass material and distances, mesh culling, water, bloom). Without this the machines that
    // need help most spent their first seconds rendering the full-quality scene, because the FPS
    // monitor needs three consecutive bad samples before it acts.
    //
    // `PerformanceMonitor` keeps adapting from here in BOTH directions — so a strong machine that
    // was misjudged climbs back to `high` (and its full authored quality) within a few good
    // seconds, and a weak one keeps dropping. Detection only has to be roughly right.
    this.performance.setInitialTier(this.caps.tier);
    this.renderer.applyQualityTier(this.caps.tier);
    this.applyQualityProfile(this.caps.tier);

    // White-smoke transition (used between scroll sections + optionally the intro).
    this.sceneTransition = new SceneTransition();
    // Opening camera fly-in. Stone interaction stays OFF until it settles so the viewer
    // just watches the move (re-enabled in the completion callback below).
    this.introSequence = new IntroSequence(this.scrollCamera, this.scroll);
    if (settings.intro.autoplay.enabled && this.stoneInteraction) {
      this.stoneInteraction.enabled = false;
    }

    // MAGICAL DISSOLVE REVEAL (2026-07-19, user: "after preloader i want animations one by one
    // with shaders, animate my whole scene objects and reveal them with progress reveal effect,
    // more like magical … this happens between 0 to 40 frames").
    //
    // Registers ONLY model scene 1 ("you don't have to load other scene mesh") — the later
    // chapters are hidden by the progressive-reveal system anyway, and patching them would make
    // them dissolve in behind the camera for nothing. The sweep runs over the SHARED bounding box
    // of scene 1 so the chapter reveals as one body, and is played from `introSequence.start`
    // below so it runs across the 0→40 camera fly-in rather than on a timer of its own.
    if (settings.intro.enabled) {
      // Guarded: a failure here must never strand the preloader (see the note on `finish` below).
      try {
        this.reveal = new RevealEffect();
        this.reveal.register(this.world.sceneGroups.get(1) ?? []);
      } catch (error) {
        this.reveal = undefined;
        this.log.error('reveal effect failed to register — continuing without it', error);
      }
    }

    // Height fog AFTER the reveal patch so it chains over it (geometry dissolves into
    // the cloud sea at its base). No-op when no model/placeholder or fog disabled.
    // Guarded for the same reason as the warm-up render below: it CHAINS onto materials the
    // reveal just patched, so it is exactly the kind of step that can throw and strand the
    // preloader at 92%.
    try {
      this.world.applyHeightFog();
    } catch (error) {
      this.log.error('height fog failed — continuing without it', error);
    }

    // Give the cloud depth prepass its initial size (it's built 1x1). Without this the very
    // first frames would sample a 1x1 depth texture and the puffs would fade wrongly.
    this.world.resizeSceneDepth();

    if (isDebug) {
      try {
        await this.initDebug();
      } catch (error) {
        this.log.error('debug panel failed to init — continuing without it', error);
      }
    }

    // Wait for the textures the material resolver started during `world.build()` (the baked
    // atlases + foliage). They are NOT in the manifest, so `loadAll` above returned before they
    // existed — without this the preloader would clear while the architecture was still
    // untextured. Safety-timed, so a wedged request degrades instead of hanging the gate.
    await this.assets.whenIdle();

    // Warm up: compile shaders + one render to avoid first-frame stutter.
    //
    // GUARDED (2026-07-19, user: "the preloader stuck on 92%"). `Preloader.setProgress` caps the
    // bar at 0.92 until `finish()` runs, so 92% is precisely the "init never reached finish()"
    // state. Everything between the asset load and `finish()` used to be unprotected: one throw
    // in shader compilation, a material patch, or the debug panel left the user staring at a
    // frozen bar forever, with the real error visible only in a console they never open. A
    // degraded scene beats a permanent hang — log it and carry on to `finish()` regardless.
    try {
      this.renderer.compile(this.sceneManager.scene, this.camera.instance);
      this.render();
    } catch (error) {
      this.log.error('warm-up render failed — starting anyway', error);
    }

    this.start();

    // Boot: fade the preloader out. The cinematic fly-in + front-end UI are OPT-IN via
    // `intro.autoplay` (reverted 2026-07-13 — kept the preloader only). When off, the scene
    // just runs directly: all scenes visible, scroll scrubs the camera from frame 0.
    void this.preloader.finish().then(() => {
      this.startAmbient(); // (dormant) global track
      this.startSceneAudio(); // per-chapter beds + spatial loops fade in as the preloader clears
      // Kick the shader dissolve off WITH the fly-in so the two read as one move: the camera
      // travels 0→40 while the scene materialises bottom-up beneath it.
      this.reveal?.play();
      this.playCinematicIntro(); // clouds fade in, water settles — see the method

      if (settings.intro.autoplay.enabled) {
        this.introSequence!.start(() => {
          this.ui.reveal();
          this.ui.setSection(0); // Chapter 1 is the default section — never crosses a boundary
          this.sceneAudio.setSection(1);
          this.applyFogForSection(0);
          if (this.stoneInteraction) this.stoneInteraction.enabled = settings.stones.enabled;
        });
      } else {
        this.ui.reveal();
        this.ui.setSection(0);
        // No fly-in to hand control over — release the boot lock (see init's lockInput) here.
        this.scroll.unlockInput();
      }
    });
    this.log.info('ready.');
  }

  private async initDebug(): Promise<void> {
    const { Debug } = await import('@/debug/Debug');
    this.debug = new Debug(this);
  }

  /** Load the looping global ambient track (if configured) so it's ready to fade in the moment
   *  the preloader clears. No-op until the user provides a file in `settings.audio.ambient.src`. */
  private setupAmbient(): void {
    const amb = settings.audio.ambient;
    if (!this.audio.enabled || !amb.enabled || !amb.src) return;
    this.audio.load('ambient', { src: [amb.src], loop: amb.loop, volume: amb.volume, rate: amb.rate });
  }

  /** Fade the ambient track in. Called after the preloader; also re-tried on the user's first
   *  gesture, since browsers block audio until then (`playWithFade` no-ops if already playing). */
  private startAmbient(): void {
    const amb = settings.audio.ambient;
    if (!this.audio.enabled || !amb.enabled || !amb.src) return;
    const startIt = () => {
      this.audio.playWithFade('ambient', amb.volume, amb.fadeIn * 1000);
      // EQ after play, once the Web Audio graph exists (no-ops harmlessly at 0 dB).
      if (amb.bass !== 0) this.audio.setBass('ambient', amb.bass, amb.bassFreq);
    };
    startIt();
    // Autoplay fallback: if the post-preloader attempt was blocked (no gesture yet), start on
    // the first interaction. `{ once: true }` + the `playing()` guard make it fire exactly once.
    const kick = startIt;
    window.addEventListener('pointerdown', kick, { once: true });
    window.addEventListener('wheel', kick, { once: true, passive: true });
    window.addEventListener('keydown', kick, { once: true });
  }

  /** Start the scene-audio engine (beds + spatial + wind) once the preloader clears, with a
   *  first-gesture fallback for the browser autoplay block (same pattern as the ambient). */
  private startSceneAudio(): void {
    if (!this.audio.enabled || !settings.audio.scenes.enabled) return;
    const kick = () => this.sceneAudio.start();
    kick();
    window.addEventListener('pointerdown', kick, { once: true });
    window.addEventListener('wheel', kick, { once: true, passive: true });
    window.addEventListener('keydown', kick, { once: true });
  }

  /**
   * Apply a quality tier's profile (`settings.performance.profiles`) — the real cost ladder for
   * weak / software-WebGL devices. Called at boot from the GPU probe and at runtime by the FPS
   * monitor, plus the debug GUI's force buttons.
   *
   * Only the LIVE-SAFE levers are touched here. `puffs`/`grassBlades` are deliberately NOT applied
   * at runtime: changing them means re-scattering hundreds of sprites / tens of thousands of
   * blades, which hitches — exactly what you don't want on a device that's already struggling.
   * Those are applied once at boot instead (see `init`).
   */
  applyQualityProfile(tier: 'high' | 'medium' | 'low'): void {
    const p = settings.performance.profiles[tier];

    // 1. Water: resolution + (low tier) stop re-rendering the reflection entirely. Biggest win —
    //    each in-view pool otherwise re-renders the whole scene every frame.
    for (const water of this.world.waters) water.setQuality(tier);

    // 1b. Auto-LOD reach: weaker tiers pull the reduced-detail distances closer to the camera.
    settings.lod.distanceScale = p.lodDistanceScale;

    // 2. Cloud soft particles: the depth prepass is another full scene render.
    settings.cloudSea.soft.enabled = p.softParticles;

    // 3. Bloom resolution.
    settings.selectiveBloom.resolutionScale = p.bloomScale;
    this.selectiveBloom.setSize(this.sizes.state);

    // 4. GRASS (2026-08-17) — pull the culling/LOD distances in, and on the LOW tier swap the
    //    blades onto the cheap Lambert material. Grass is the scene's dominant fragment cost, so
    //    this is the lever that actually moves an integrated GPU. Both are live-safe: no
    //    re-scatter, just distances and a material reference.
    this.world.grass?.setQualityTier(tier);

    // 5. SCREEN-SIZE CULLING reach — weaker tiers hide meshes below a larger pixel threshold,
    //    cutting draw calls across every pass (prepass + main + bloom + reflections). HIGH sets
    //    this to 0, which switches the culler off outright: a capable machine draws everything.
    settings.visibilityCulling.minPixelSize = p.cullPixelSize;

    // 6. RENDER SCALE — the bluntest fill-rate lever there is, and the one that matters most on a
    //    machine with no discrete GPU. The adaptive monitor also drives this via
    //    `Renderer.applyQualityTier`; setting it here means a tier chosen at BOOT (from the GPU
    //    probe) gets the same treatment without waiting for three bad seconds of measurement.
    this.renderer.setPixelRatioScale(p.pixelRatioScale);

    // Keep the offscreen buffers in step with the renderer's new effective pixel ratio.
    this.post.resize(this.sizes.state);
    this.world.resizeSceneDepth();
    this.log.info(`quality → ${tier} (reflections ${p.reflections ? 'on' : 'frozen'}, bloom ${p.bloomScale}).`);
  }

  /** Wire the navigation UI's callbacks to the world/audio/scroll, and load the UI SFX. */
  private setupUiHandlers(): void {
    const uiBase = settings.audio.scenes.base + '/UI_Elements';
    if (this.audio.enabled) {
      this.audio.load('ui-cp-hover', { src: [encodeURI(`${uiBase}/Checkpoints/CheckPoint_hover.ogg`)], volume: 0.5, pool: 3 });
      this.audio.load('ui-cp-click', { src: [encodeURI(`${uiBase}/Checkpoints/CheckPoint_Click.ogg`)], volume: 0.6, pool: 3 });
      this.audio.load('ui-about-open', { src: [encodeURI(`${uiBase}/About/About_Open.ogg`)], volume: 0.7 });
      this.audio.load('ui-about-close', { src: [encodeURI(`${uiBase}/About/About_Close.ogg`)], volume: 0.7 });
      this.audio.load('ui-music', { src: [encodeURI(`${uiBase}/Music_Toggle.ogg`)], volume: 0.7 });
      this.audio.load('ui-enter', {
        src: [encodeURI(`${uiBase}/Page_loader_Button_onclick.ogg`)],
        volume: 0.8,
      });
    }

    // ENTER click on the preloader: play its SFX. The click is also what unlocks the browser
    // audio context, so everything started right after it is reliably audible.
    this.preloader.onEnter = () => {
      if (this.audio.enabled) this.audio.play('ui-enter');
      // BOOKEND SMOKE (2026-07-23): the user's smoke-burst clip plays over the scene as the
      // preloader clears and the 0→40 fly-in departs — its ~2s dissipation IS the emergence.
      void this.videoTransition?.play();
    };

    this.ui.onUiSound = (name) => {
      const map: Record<string, string> = {
        'checkpoint-hover': 'ui-cp-hover',
        'checkpoint-click': 'ui-cp-click',
        'about-open': 'ui-about-open',
        'about-close': 'ui-about-close',
        'music-toggle': 'ui-music',
        // KNOW MORE click (2026-09-01) — reuses the checkpoint click, the site's established
        // "a control answered" sound.
        'know-more': 'ui-cp-click',
      };
      if (this.audio.enabled) this.audio.play(map[name]!);
    };
    this.ui.onCheckpoint = (i) => this.jumpToSection(i);
    this.ui.onMusicToggle = (on) => Howler.mute(!on);
    // Pause the stone hover interaction while the About panel is open (camera is blurred),
    // and FREEZE scroll (2026-07-23, user: "when about section is active user can't able to
    // scroll") — the world must not drift behind the blur. On close, only unlock if nothing
    // else (a magnet glide / checkpoint transition) currently owns the lock.
    this.ui.onAboutToggle = (open) => {
      this.aboutOpen = open;
      // About and a story are both full-screen takeovers — opening one closes the other rather
      // than stacking two narratives on top of each other.
      if (open) this.storyMode?.forceClose();
      if (this.stoneInteraction) this.stoneInteraction.enabled = !open && settings.stones.enabled;
      if (open) this.scroll.lockInput();
      else if (!this.magnet.active && !this.sectionTransitioning) this.scroll.unlockInput();

      // (The brief "About opens into shader mode" experiment was reverted same day —
      // 2026-09-02, user: "don't take About to shader mode, I want the blur instead". The
      // About's immersion now comes from its own flowing sky-shader backdrop canvas — see
      // `ui/AboutSky.ts` — layered over the original heavy camera blur.)
    };

    // KNOW MORE — one click, every device (2026-08-18 v5: the press-and-hold ring is gone).
    this.ui.onKnowMore = (index) => this.storyMode?.openChapter(index);
  }

  /** Nav-rail checkpoint → jump straight to a chapter (immediate scroll + reveal). Re-arms the
   *  magnets relative to the new position so they still fire on subsequent forward scrolling. */
  /**
   * Nav-rail checkpoint click → CINEMATIC jump (2026-07-20, user: "wherever the camera is, you
   * just pause the camera, move the white transition, and then load the starting scene of that
   * section — I want it very smooth").
   *
   * Flow: freeze the camera exactly where it is (scroll locked + pinned, same defence as the
   * magnets), play the white flash, and UNDER ITS PEAK teleport scroll/camera/scene/UI/audio to
   * the destination chapter's start frame — so the cut is never visible, the white simply clears
   * on the new chapter. Input returns after the flash plus the usual settle beat.
   *
   * (Until now this was a bare instant `scrollTo` — a visible hard cut.)
   */
  private jumpToSection(index: number): void {
    const section = SECTIONS[index];
    if (!section) return;
    // One transition at a time — ignore checkpoint clicks while a flash (nav or boundary) is
    // already covering the screen; a second swap mid-flash would fight the first.
    if (this.sectionTransitioning) return;
    // A magnet mid-glide surrenders to an explicit navigation choice.
    this.magnet.active = false;
    this.magnet.point = null;
    this.sectionTextCall?.kill();
    this.sectionTextCall = null;

    // INSTANT RESPONSE AT THE CLICK (2026-07-23 UX pass, user: "when I click I want it to
    // immediately give the response that it's on that section"): the rail's current diamond
    // moves to the destination NOW — the click is answered in the same frame — and the
    // current chapter's words leave FAST (short fades, no exit shimmer), gone before the
    // smoke rises. The arrival reveal (finishMagnet, at the stop) is always a fresh
    // animate-in, never a leftover.
    this.ui.setRailCurrent(index);
    this.ui.hideSection(true);

    // GLIDE-IN ARRIVAL (2026-07-21, user: "when I click checkpoints I want the camera to
    // arrive smoothly"). Under the FULL CLOUD COVER the camera TELEPORTS to a few frames
    // BEFORE the chapter's start (snapped — invisible), and a synthetic magnet then glides
    // those last frames in as the clouds part: the viewer never sees a cut, just the camera
    // flowing into the chapter, with the text animating in once it stops (finishMagnet).
    const PRE_FRAMES = 8;
    const applyJump = (): void => {
      const destFrac = (section.startFrame + 0.5) / TOTAL_FRAMES;
      MAGNETIC_POINTS.forEach((p, k) => {
        this.magnetArmed[k] = destFrac < p.triggerFrame / TOTAL_FRAMES;
      });
      const preFrame = Math.max(section.startFrame - PRE_FRAMES, 0);
      const preFrac = preFrame / TOTAL_FRAMES;
      const progress = THREE.MathUtils.clamp(this.clipToProgress(preFrac), 0, 1);
      const target = progress * this.scroll.state.limit;
      this.scroll.scrollTo(target, { immediate: true, force: true });
      this.sectionLockScroll = target;
      this.scrollCamera.resetSmoothing();
      // Snap the camera onto the pre-frame pose UNDER the white — no cross-map easing.
      this.scrollCamera.sampleAt(preFrac);
      this.cameraController.commit();

      // Chapter ambience now; text waits for the glide's stop.
      this.currentSectionIndex = index;
      if (!settings.scenes.loadAllAtOnce) {
        if (settings.scenes.mode === 'exclusive') this.world.showSceneExclusive(section.sceneIndex);
        else this.world.setActiveScene(section.sceneIndex);
      }
      this.cameraController.targetFov = this.camera.computeFovForFocalLength(section.focalLength);
      this.sectionState.name = section.name;
      this.sceneAudio.setSection(index + 1);
      this.applyFogForSection(index);

      // Hand ownership to a synthetic magnet for the final drift (it pins, settles, unlocks
      // and fires the text at the stop — identical language to the scroll-triggered pulls).
      this.sectionTransitioning = false;
      this.magnet.point = {
        name: `Checkpoint ${index + 1}`,
        triggerFrame: preFrame,
        destFrame: section.startFrame,
        flash: false,
        sectionIndex: index,
        // Only ~8 frames of drift — keep the checkpoint arrival at its own brisk-calm pace
        // rather than inheriting the (2026-09-01, much longer) scroll-magnet glide time,
        // which would turn this short settle into a crawl.
        durationSec: 3.2,
      };
      this.magnet.active = true;
      this.magnet.elapsed = 0;
      this.magnet.fromFrac = preFrac;
      this.magnet.toFrac = section.startFrame / TOTAL_FRAMES;
      this.magnet.arrived = false;
      this.magnet.fovApplied = true; // fov already set above
      this.magnet.zoomFromFocal = null; // …and snapped under the cloud cover — no continuous zoom
      this.magnet.durOverride = null;
      // The camera is STILL under the white — start the drift from rest, not from whatever
      // scroll velocity the LAST magnet grabbed at (a stale v0 made some arrivals lurch).
      this.magnet.v0 = 0;
    };

    // Freeze where we are: lock + pin (shares the section-transition pin in `onTick`, which
    // discards any input that leaks past `lenis.stop()` — see `pinScrollTo`'s note).
    this.sectionTransitioning = true;
    this.sectionLockScroll = this.scroll.state.scroll;
    this.scroll.lockInput();
    this.sceneAudio.playTransition();

    // CLOUD ENVELOPE (2026-07-23, user: "same cloud thing for checkpoints — I don't like the
    // white flash"): the CameraFog closes over the frozen frame (forced mode — ~1s), the
    // teleport happens fully inside the clouds after a short held beat, and on release the
    // frame-driven map (clear at any mid-clip frame) PARTS the clouds while the synthetic
    // magnet glides the final frames in — arriving at every chapter the same way the whole
    // site begins: out of the sky. White flash kept only as a fallback if the fog is absent.
    const fog = settings.cameraFog.enabled ? this.world.cameraFog : null;
    if (fog) {
      fog.envelop();
      const waitCover = (): void => {
        if (!fog.covered) {
          gsap.delayedCall(0.1, waitCover);
          return;
        }
        // A breath fully inside the clouds, then swap the world underneath them.
        gsap.delayedCall(0.35, () => {
          applyJump();
          fog.release();
        });
      };
      waitCover();
      return;
    }
    if (this.sceneTransition) {
      // No unlock here: applyJump (at the peak) hands ownership to the synthetic magnet, whose
      // finishMagnet unlocks + fires the text once the glide-in settles.
      void this.sceneTransition.play(applyJump);
      return;
    }
    applyJump();
  }

  /**
   * The story open/close IMPULSE (2026-09-02 v2, user: "just keep the camera thing — the
   * camera goes a little back, with easing fast and slow"). One clean move: the camera steps
   * BACK from wherever it stands as the dark ripples out — quick to begin, long to settle
   * (power3.out), held for the whole story, eased home on close. Also seeds the colour
   * ripple's world-space origin: the change spreads outward FROM THE VIEWER (see
   * `StoryGrade.rippleOrigin`).
   */
  private applyStoryImpulse(open: boolean): void {
    const cfg = settings.storyMode.recoil;
    if (open) this.world.storyGrade.rippleOrigin.value.copy(this.camera.instance.position);
    this.recoilTween?.kill();
    this.recoilTween = gsap.to(this.storyRecoil, {
      value: open ? 1 : 0,
      // "Fast and slow": the step back MOVES immediately then settles long; the return is a
      // gentler two-sided ease so coming home reads as release, not a push.
      duration: open ? cfg.inSec : cfg.outSec,
      ease: open ? 'power3.out' : 'power2.inOut',
      onUpdate: () => {
        this.cameraController.dolly = this.storyRecoil.value * cfg.distance;
      },
      onComplete: () => {
        this.cameraController.dolly = (open ? 1 : 0) * cfg.distance;
      },
    });
  }

  /** Resolve each spatial source's mesh to a world position and hand it to {@link SceneAudio}, so
   *  the water / emblem loops emit from the right place in 3D. */
  private setupSceneAudioPositions(): void {
    const root = this.world.contentRoot;
    if (!root) return;
    root.updateWorldMatrix(true, true);
    const wanted = new Set(settings.audio.scenes.spatial.map((s) => s.mesh));
    const pos = new THREE.Vector3();
    root.traverse((object) => {
      const raw = (object.userData.rawName as string | undefined) ?? object.name;
      if (wanted.has(raw) && (object as THREE.Mesh).isMesh) {
        object.getWorldPosition(pos);
        this.sceneAudio.setMeshPosition(raw, pos);
      }
    });
  }

  /** Connect the 5-family {@link StoneAudio} engine to the interaction's per-family lift/settle
   *  events. StoneAudio handles the loudness / pitch / delay itself, so this is just the routing. */
  private setupStoneAudio(): void {
    if (!this.audio.enabled || !this.stoneInteraction) return;
    this.stoneInteraction.onStoneSound = (family, kind, intensity) => {
      this.stoneAudio.play(family, kind, intensity);
    };
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.time.start();
  }

  stop(): void {
    this.started = false;
    this.time.stop();
  }

  /** Detect scroll-progress crossing into a new `SceneSections` boundary and react:
   *  swap mesh visibility (`World.setActiveScene`) + ease the camera's focal length,
   *  either hidden behind the white-smoke `SceneTransition` (most boundaries) or
   *  applied immediately as a plain continuous cut (the Scene 3 -> 4 boundary only). */
  private updateSection(): void {
    // THE CAMERA'S fraction, not raw scroll (2026-09-01, no-magnets pass): a hard flick jumps
    // raw scroll seconds ahead of the speed-capped camera, and the chapter text/audio used to
    // swap while the viewer was still watching the previous chapter. The world state follows
    // what is actually ON SCREEN.
    const clipFraction = this.scrollCamera.lastSampledFraction;
    const { section, index } = resolveSection(clipFraction);
    if (index === this.currentSectionIndex || this.sectionTransitioning) return;
    const prevIndex = this.currentSectionIndex;
    this.currentSectionIndex = index;

    // THE RESTART ZONE (frames 300+, the pseudo-section with no chapter copy of its own):
    // crossing INTO it fades Chapter 5's words out and shows nothing more — the journey's
    // text is done, the outro flight plays clean (2026-07-23, user: "at 300 I want my content
    // fade out and it goes further"; also fixes the double-load — index 5 clamps to Chapter
    // 5's copy, so the old path re-animated the words the user was already reading). Crossing
    // back OUT of it (reverse scroll below 300) brings Chapter 5's words back in.
    const chapterCount = settings.ui.chapters.length;
    const intoRestartZone = index >= chapterCount;
    const fromRestartZone = prevIndex >= chapterCount;

    const loadAll = settings.scenes.loadAllAtOnce;

    const apply = () => {
      // Progressive reveal along the timeline (2026-07-17). `cumulative` keeps every scene
      // you've already flown through visible behind you; `exclusive` shows only this one
      // (Scenes 3+4 together). See `settings.scenes.mode`.
      if (!loadAll) {
        if (settings.scenes.mode === 'exclusive') this.world.showSceneExclusive(section.sceneIndex);
        else this.world.setActiveScene(section.sceneIndex);
      }
      this.cameraController.targetFov = this.camera.computeFovForFocalLength(section.focalLength);
      this.sectionState.name = section.name;
      // SMOOTH TEXT SWAP for non-magnet crossings — i.e. scrolling BACKWARD over a boundary
      // (forward boundaries are owned by the magnets). Mirrors the forward choreography
      // (2026-07-21, user: "when the user scrolls back it will reverse the same thing"): the
      // leaving chapter's text fades out, and after a short beat the entered chapter's text
      // animates in. Guarded so rapid multi-boundary scrubbing only lands the final chapter.
      if (intoRestartZone) {
        // Words out, nothing in — the outro belongs to the world, not the copy.
        this.syncShimmerToScroll();
        this.ui.hideSection();
        this.sectionTextCall?.kill();
        this.sectionTextCall = null;
      } else if (prevIndex === -1 || fromRestartZone ||
        Math.min(index, chapterCount - 1) !== Math.min(prevIndex, chapterCount - 1)) {
        this.syncShimmerToScroll();
        this.ui.hideSection();
        this.sectionTextCall?.kill();
        this.sectionTextCall = gsap.delayedCall(0.55, () => {
          this.sectionTextCall = null;
          if (this.currentSectionIndex === index && !this.magnet.active) this.ui.setSection(index);
        });
      }
      // Crossfade the chapter's music bed / wind to this section (1-based section number).
      this.sceneAudio.setSection(index + 1);
      this.applyFogForSection(index);
    };

    // Transition swell under the white flash (only where the section actually flashes).
    if (settings.scenes.transition && section.transition && !loadAll) {
      this.sceneAudio.playTransition();
    }

    // The white-smoke flash is OFF by default (`settings.scenes.transition`) — the user
    // rejected it twice. With it off (or everything loaded up front) a boundary is just a
    // plain cut that eases the focal length.
    if (settings.scenes.transition && section.transition && this.sceneTransition && !loadAll) {
      // Lock scroll input for the whole transition PLUS a short settle buffer after
      // it clears — the camera keeps easing toward its (now-frozen) target the whole
      // time, so it visibly decelerates into place instead of the user being able to
      // yank it further mid-transition (2026-07-05, user request).
      this.sectionTransitioning = true;
      this.sectionLockScroll = this.scroll.state.scroll;
      this.scroll.lockInput();
      void this.sceneTransition.play(apply).then(() => {
        setTimeout(() => {
          this.scroll.unlockInput();
          this.sectionTransitioning = false;
        }, SECTION_SETTLE_MS);
      });
    } else {
      apply();
    }
  }

  /** Clip fraction (0-1) ↔ scroll progress (0-1), accounting for the intro window. */
  private clipToProgress(frac: number): number {
    const o = this.scrollCamera.introStartFraction;
    return o >= 1 ? 0 : (frac - o) / (1 - o);
  }

  /** Detect the camera crossing a magnetic trigger frame (forward) and grab it: lock scroll and
   *  begin the auto-glide to the destination. Re-arms a point once the user is back before it.
   *  ALSO detects the mirror crossing (2026-07-23, user: "when they make reverse scrolling —
   *  there also we have a magnet zone, the user can't break those frames quickly"): scrolling
   *  BACKWARD over a magnet's destination frame grabs the camera and glides it back to just
   *  before the trigger — the same choreographed travel, both directions. */
  private checkMagnetTrigger(clipFraction: number, prevClip: number | null): void {
    // NO-MAGNETS MODE (2026-09-01, user: "let's try to remove it and have normal scrolling"):
    // the scroll-triggered pulls are skipped entirely — only the RESTART loop point still
    // grabs (it's the replay mechanic, not a pull). Flip `settings.scenes.magneticPulls` to
    // bring the full choreography back.
    const pullsOn = settings.scenes.magneticPulls;
    for (let i = 0; i < MAGNETIC_POINTS.length; i += 1) {
      const p = MAGNETIC_POINTS[i]!;
      if (!pullsOn && !p.restart) continue;
      const trigFrac = p.triggerFrame / TOTAL_FRAMES;
      if (!this.magnetArmed[i]) {
        // Re-arm once the user has scrolled back comfortably before the trigger.
        if (clipFraction < trigFrac - 0.01) this.magnetArmed[i] = true;
        continue;
      }
      if (clipFraction >= trigFrac) {
        this.startMagnet(i, clipFraction);
        return;
      }
    }
    // REVERSE zones — one per non-restart point, armed while the user sits past its
    // destination, fired on a backward crossing of that destination frame.
    if (!pullsOn || prevClip === null) return;
    for (let i = 0; i < MAGNETIC_POINTS.length; i += 1) {
      const p = MAGNETIC_POINTS[i]!;
      if (p.restart) continue; // the outro loop only ever runs forward
      const destFrac = p.destFrame / TOTAL_FRAMES;
      if (!this.reverseArmed[i]) {
        if (clipFraction > destFrac + 0.01) this.reverseArmed[i] = true;
        continue;
      }
      if (prevClip >= destFrac && clipFraction < destFrac && this.clipVel < 0) {
        this.startReverseMagnet(i, clipFraction);
        return;
      }
    }
  }

  /**
   * The mirror grab: the user scrolled BACKWARD over a chapter's arrival frame, so the camera
   * is pulled the rest of the way back to just before that magnet's trigger — landing in the
   * PREVIOUS chapter with its ambience, text and focal, exactly like a forward pull in
   * reverse. Reuses the whole magnet state machine via a synthetic point.
   */
  private startReverseMagnet(index: number, fromFrac: number): void {
    const p = MAGNETIC_POINTS[index]!;
    // Land far enough before the trigger that the forward magnet re-arms (its re-arm margin
    // is 0.01 of the timeline ≈ 3.7 frames) — 6 frames clears it with room.
    const backFrame = Math.max(p.triggerFrame - 6, 0);
    // Grab from the CAMERA's fraction, same as the forward grab (2026-09-01 v2): raw scroll can
    // be well behind/ahead of the capped camera on a hard flick, and the glide must start from
    // what's on screen.
    fromFrac = this.scrollCamera.lastSampledFraction;
    if (fromFrac <= backFrame / TOTAL_FRAMES) return;
    const prevSection = Math.max(p.sectionIndex - 1, 0);

    this.syncShimmerToScroll();
    this.ui.hideSection();
    this.sectionTextCall?.kill();
    this.sectionTextCall = null;
    this.reverseArmed[index] = false;

    this.magnet.active = true;
    this.magnet.point = {
      name: `${p.name} (reverse)`,
      triggerFrame: p.destFrame,
      destFrame: backFrame,
      // Apply the previous chapter's focal at the grab (frac starts above the trigger, so the
      // focal window condition is already true) — the controller eases it across the glide.
      focalFrame: p.triggerFrame,
      flash: false,
      sectionIndex: prevSection,
    };
    this.magnet.elapsed = 0;
    this.magnet.fromFrac = fromFrac;
    this.magnet.toFrac = backFrame / TOTAL_FRAMES;
    this.magnet.arrived = false;
    this.magnet.fovApplied = false;
    this.magnet.zoomFromFocal = null;
    this.magnet.durOverride = null;
    this.magnet.v0 = Math.min(this.clipVel, 0); // backward component only (span is negative)
    this.scroll.lockInput();
    // Previous chapter's AMBIENCE (music bed, fog, rainbow gate) crossfades during the glide;
    // its text waits for the stop, same as every pull.
    this.applyMagnetArrival(this.magnet.point);
  }

  private startMagnet(index: number, fromFrac: number): void {
    const p = MAGNETIC_POINTS[index]!;
    // The leaving chapter's TEXT fades out the moment the pull grabs (2026-07-21 choreography)
    // — the words exit first, then the camera moves. Shimmer pace follows the user's energy.
    // The RESTART grab exits FAST instead (2026-07-23): the smoke rises quickly at 320 and the
    // reset fires the moment it mats, so a slow cinematic exit would still be mid-fade at the
    // teleport — Chapter 5's title must be gone before the loop resets.
    this.syncShimmerToScroll();
    this.ui.hideSection(p.restart);
    this.sectionTextCall?.kill();
    this.sectionTextCall = null;
    this.magnetArmed[index] = false;

    // GRAB FROM THE CAMERA (2026-09-01 v2; replaces both the 2026-07-19 skip and the raw-frac
    // grab). Raw scroll can be seconds AHEAD of the speed-capped camera after a hard flick;
    // gliding from the raw position teleported the camera forward at the grab. The glide now
    // always starts from where the camera actually IS (`lastSampledFraction`) — visually
    // continuous by construction, and the per-frame scroll pin drags the leaked raw position
    // back onto the glide. Only if the camera is genuinely at/past the destination is there
    // nothing left to pull. (`fromFrac` — the raw crossing — is only what triggered us.)
    void fromFrac;
    const destFrac = p.destFrame / TOTAL_FRAMES;
    const grabFrac = this.scrollCamera.lastSampledFraction;
    if (!p.restart && grabFrac >= destFrac - 1e-4) return;
    this.magnet.active = true;
    this.magnet.point = p;
    this.magnet.elapsed = 0;
    this.magnet.fromFrac = grabFrac;
    this.magnet.toFrac = destFrac;
    this.magnet.arrived = false;
    this.magnet.fovApplied = false;
    this.magnet.zoomFromFocal = null;
    // Forward component only — and capped to the camera's own speed limit: the C¹ velocity
    // match should continue what the VIEWER saw (the capped camera), not the raw scroll delta
    // of a violent flick, which would launch the glide with a felt kick.
    const capVel = settings.camera.maxFramesPerSec > 0
      ? settings.camera.maxFramesPerSec / TOTAL_FRAMES
      : Number.POSITIVE_INFINITY;
    this.magnet.v0 = Math.min(Math.max(this.clipVel, 0), capVel);
    this.scroll.lockInput();

    // RESTART (2026-07-23 v4): the camera GLIDES 300→370 to the clip's very end; the CLOUD
    // ENVELOPE closes over the lens across frames 338→352 (CameraFog, frame-driven), and the
    // reset to frame 0 happens fully inside the clouds (see updateMagnet). Nothing else to do
    // at the grab: the ambience of chapter 5 simply persists through the outro glide.
    if (p.restart) {
      // The camera-based grab can start this glide from FAR behind the trigger (a hard flick
      // near the end while the capped camera is still chapters back). Scale the duration to
      // the actual span so the outro sweep keeps its authored ~25 frames/sec pace instead of
      // blasting a 200-frame span through every scene in 2.8s (2026-09-01 v2).
      const spanFrames = (destFrac - grabFrac) * TOTAL_FRAMES;
      this.magnet.durOverride = Math.max(p.durationSec ?? 2.8, spanFrames / 25);
      return;
    }
    this.magnet.durOverride = null;

    // Chapter AMBIENCE (music bed, fog, rainbow gate, scene visibility) switches at the grab so
    // it crossfades DURING the glide — but the TEXT does not: it animates in only when the
    // camera has fully stopped (finishMagnet), per the user's spec.
    this.applyMagnetArrival(p);
  }

  /** Drive the camera along the eased glide each frame while a magnet is active. */
  private updateMagnet(tick: TickState): void {
    const p = this.magnet.point;
    if (!p) return;
    const dur = Math.max(
      this.magnet.durOverride ?? p.durationSec ?? settings.scenes.magnetDurationSec,
      0.1,
    );
    this.magnet.elapsed += tick.delta;
    const t = Math.min(this.magnet.elapsed / dur, 1);
    // VELOCITY-MATCHED EASE (2026-07-22, user: "the acceleration should be as smooth as the
    // user's own scrolling — they shouldn't even know they're being pulled"). A cubic Hermite
    // whose INITIAL slope equals the user's scroll velocity at the grab (normalized to the
    // glide's span/duration) and whose final slope is zero: the hand-over from scrolling to
    // being pulled is C¹-continuous — no hitch, no felt acceleration step — and the arrival
    // still glides to a perfect stop. m0 is clamped ≤ 2.2 to keep the curve monotonic (a
    // violent flick otherwise makes Hermite overshoot past the destination).
    const span = this.magnet.toFrac - this.magnet.fromFrac;
    // TANGENTS (2026-09-01 v3, user: "pulling starting slow and ending fast — that kind of
    // easing; from 40 the doors and camera work smooth and the end is smooth-fast"). The curve
    // is now a clean accelerating pull: a gentle take-off (m0 clamped LOW so even a scrolling
    // user feels the pull begin softly, not launch) building into a confident, still-moving
    // finish (m1 > 1 = arrival above average speed). The settle beat + the controller's own
    // smoothing absorb the landing, so "fast at the end" stays smooth, never a slam.
    // (History: m1 0.65 → 0.2 "imperceptible" → 0.42 "normal" → 1.3 "slow start, fast end".)
    const m0 = THREE.MathUtils.clamp(span !== 0 ? (this.magnet.v0 * dur) / span : 0, 0, 0.5);
    const m1 = 1.3;
    const t2 = t * t;
    const t3 = t2 * t;
    // hermite: h(0)=0, h(1)=1, h'(0)=m0 (velocity-matched grab), h'(1)=m1 (soft fast finish)
    const eased = m0 * (t3 - 2 * t2 + t) + (-2 * t3 + 3 * t2) + m1 * (t3 - t2);
    const frac = this.magnet.fromFrac + span * eased;
    this.scrollCamera.sampleAt(frac);

    // CONTINUOUS ZOOM (2026-09-01, user: "when the focal length changes it's stuttering …
    // still not smooth — make it flow, feel natural"). Three rules, each earned by a failed
    // simpler version:
    //  - Over the ENTIRE glide's TIME as a smoothstep (a one-shot targetFov handoff put the
    //    zoom on the exponential ease, whose first frame is its fastest = visible kick; the
    //    old 60→64 frame window compressed the zoom into the fast tail).
    //  - In LOG-FOCAL space, not FOV degrees: a constant zoom *ratio* per unit progress is how
    //    a physical lens pull moves — degree-space lerp warps the perceived rate mid-zoom.
    //  - Applied DIRECTLY (`setFovImmediate`): the value is already smooth per-frame, and the
    //    controller's own fov ease on top only added lag — the lens visibly kept zooming for
    //    ~2s after the camera had stopped.
    // (`focalFrame` no longer paces the zoom; the grab starts it, forward and reverse alike.)
    if (!this.magnet.fovApplied) {
      this.magnet.fovApplied = true;
      this.magnet.zoomFromFocal = this.camera.focalLengthForFov(this.cameraController.targetFov);
    }
    if (this.magnet.zoomFromFocal !== null) {
      const section = SECTIONS[p.sectionIndex];
      if (section) {
        const fs = t * t * (3 - 2 * t); // smoothstep over glide TIME — no onset kick
        const focal =
          this.magnet.zoomFromFocal *
          Math.pow(section.focalLength / this.magnet.zoomFromFocal, fs);
        this.cameraController.setFovImmediate(this.camera.computeFovForFocalLength(focal));
      }
    }

    // PIN the scroll to the glide EVERY FRAME — not just once at the end (2026-07-19, user:
    // "while transitions are going, this scroll thing doesn't count anymore").
    //
    // `lockInput()` (`lenis.stop()`) is NOT airtight: Lenis only `preventDefault`s wheel input
    // while stopped when the event is CANCELABLE, and touchpad momentum ("scroll fast fast
    // fast", then flick) delivers non-cancelable wheel events — those scroll the page NATIVELY,
    // and Lenis's `onNativeScroll` has no stopped-guard, so it happily syncs
    // `animatedScroll = targetScroll = actualScroll` mid-lock. Keyboard/scrollbar input leaks the
    // same way. Result: on unlock the scroll was somewhere else entirely and the camera snapped
    // there — the "coming back and again goes magnetic" bug. Re-pinning each tick (immediate +
    // force, and Lenis swallows the next native echo via `preventNextNativeScrollEvent`)
    // overwrites any leak within a frame, so the handoff is seamless BY CONSTRUCTION.
    this.pinScrollTo(frac);

    // Flash glide: fire the white transition (which swaps content at its peak) once, mid-glide.
    // Gated on the same master as the boundary flashes — see startMagnet.
    if (p.flash && settings.scenes.transition && !this.magnet.arrived && t >= settings.scenes.magnetFlashAt) {
      this.magnet.arrived = true;
      this.sceneAudio.playTransition();
      if (this.sceneTransition) void this.sceneTransition.play(() => this.applyMagnetArrival(p));
      else this.applyMagnetArrival(p);
    }

    // RESTART FINALE (2026-07-23 v4, user: "at ~340 the cloud planes cover the camera for a
    // couple of seconds, then the same 0→40 loop"): NO white flash any more — as the camera
    // glides its final frames the CameraFog envelope swallows the view (frame-driven: it
    // closes in across 338→352 automatically, see `CameraFog.targetFor`), the reset to frame 0
    // happens fully inside the clouds (finishMagnet, below, at the glide's settle), and the
    // replayed intro then EMERGES from those same clouds as they clear across 6→30. One
    // continuous flight through the sky — the world only ever changes behind the clouds.
    if (p.restart) {
      // BOOKEND VIDEO (2026-07-23 v3): once the outro glide crosses the cover frame, the
      // user's smoke-burst clip fires over the still-moving camera. Under its near-white
      // peak (~0.72s in, veil-assisted) the world resets to frame 0 and the replayed intro
      // begins — the clip's remaining ~2s of dissipation IS the emergence. The glide toward
      // 370 is just the motion under the smoke and never needs to finish.
      if (!this.magnet.arrived && frac * TOTAL_FRAMES >= settings.cameraFog.coverStartFrame) {
        this.magnet.arrived = true;
        this.sceneAudio.playTransition();
        if (this.videoTransition?.available) {
          void this.videoTransition.play(() => {
            // Guard: a checkpoint click mid-outro surrenders the magnet — don't reset then.
            if (this.magnet.active && this.magnet.point === p) this.finishMagnet(p);
          });
        }
      }
      // Fallback without the video (file missing/errored): the old glide-end + settle timing.
      if (
        !this.videoTransition?.available &&
        this.magnet.elapsed >= dur + Math.max(settings.scenes.magnetSettleSec, 0)
      ) {
        this.finishMagnet(p);
      }
      return;
    }

    // SETTLE: after the glide lands (t = 1, zero velocity by the easing), hold the lock a touch
    // longer with the camera pinned at the destination, so the user can't shove the camera the
    // instant it arrives — then hand control back and let the chapter's text animate in.
    if (this.magnet.elapsed >= dur + Math.max(settings.scenes.magnetSettleSec, 0)) {
      this.finishMagnet(p);
    }
  }

  /**
   * FOG PER CHAPTER (2026-07-21, user: "when the user goes to section 2 I want the fog to fade
   * out, very smooth"). Chapter 1 wears the boot fog (near/far from settings); every later
   * chapter tweens the SAME THREE.Fog object out to beyond-the-scene distances — a slow eased
   * dissolve, never a pop, and never `scene.fog = null` (removing the object would recompile
   * materials and can't animate back). Returning to Chapter 1 tweens it back in.
   */
  private applyFogForSection(index: number): void {
    // Chapter-scoped ambience rides the same hook: the rainbow lens ring belongs to CHAPTER 3
    // only (2026-07-21) — it fades in as the chapter begins and out as it ends, via the ring's
    // own slow CSS transition. (Called before the fog guard: the ring doesn't need fog on.)
    this.cinematic.setRainbowVisible(index === 2);

    // Per-chapter cloud/fog sea height (2026-07-23, user control) — the sea eases toward the
    // chapter's own Y as its ambience crossfades in. The restart pseudo-section (index 5)
    // keeps Chapter 5's height.
    const cloudIdx = Math.min(index, settings.ui.chapters.length - 1);
    this.world.cloudSea?.setSectionY(settings.cloudSea.sectionY[cloudIdx] ?? 0);

    const fog = this.sceneManager.scene.fog as THREE.Fog | null;
    if (!fog || !settings.fog.enabled) return;
    const inFirst = index === 0;
    gsap.to(fog, {
      near: inFirst ? settings.fog.near : 1200,
      far: inFirst ? settings.fog.far : 2000,
      duration: 2.6,
      ease: 'power2.inOut',
      overwrite: 'auto',
    });
  }

  /**
   * Set the shimmer sweep pace from the user's CURRENT scroll speed (2026-07-22, user: "the
   * shimmer has slowness and fastness according to scroll — set a max, but keep it slow").
   * Idle/gentle scrolling → the full cinematic 2.8s sweep; hard scrolling shortens it, but
   * never below the 2.0s floor — so the effect can quicken with energy without ever reading
   * frantic. Exit sweeps run proportionally shorter.
   */
  private syncShimmerToScroll(): void {
    const speed01 = THREE.MathUtils.clamp(Math.abs(this.clipVel) / 0.12, 0, 1);
    this.ui.shimmerInSec = 3.8 - speed01 * 0.8; // 3.8s calm → 3.0s at full energy — felt, never rushed
    this.ui.shimmerOutSec = 1.8 - speed01 * 0.4; // 1.8s → 1.4s
  }

  /** Force the Lenis scroll position to match a clip fraction, discarding anything the user's
   *  input leaked in this frame. See the note in {@link updateMagnet}. */
  private pinScrollTo(clipFrac: number): void {
    const progress = THREE.MathUtils.clamp(this.clipToProgress(clipFrac), 0, 1);
    this.scroll.scrollTo(progress * this.scroll.state.limit, { immediate: true, force: true });
  }

  private finishMagnet(p: MagneticPoint): void {
    this.magnet.active = false;
    this.magnet.point = null;
    if (p.restart) {
      // Loop back to the very start: reset scroll to the top and replay the intro fly-in.
      // `force` because scroll is LOCKED here (Lenis ignores scrollTo while stopped without it).
      // HARD-CLEAR any remnant of Chapter 5's text under the full smoke cover — a mid-fade
      // exit tween must never still be visible when the replayed intro emerges (2026-07-23,
      // user: "when it resets the last section title is visible at first").
      this.ui.clearSectionNow();
      this.currentSectionIndex = 0;
      this.magnetArmed.fill(true);
      this.scroll.scrollTo(0, { immediate: true, force: true });
      this.scrollCamera.resetSmoothing();
      // TELEPORT the camera to frame 0 under the FULL CLOUD COVER (frames 370 and 0 are both
      // fully enveloped, so the cut is invisible by construction) — ORDER MATTERS (2026-07-21 v3,
      // user: "the camera reset is visible, all sections whip past behind the camera"):
      // sample frame 0 FIRST so the controller's targets hold the frame-0 pose, set the 35mm
      // boot lens, THEN commit() to snap position/rotation/fov in one frame. Previously commit
      // ran against stale 370-pose targets, so the controller visibly EASED backward through
      // every scene while the white was already clearing.
      this.scrollCamera.sampleAt(0);
      this.cameraController.targetFov = this.camera.computeFovForFocalLength(SECTIONS[0]!.focalLength);
      this.cameraController.commit();
      this.scroll.unlockInput();
      this.reveal?.play(); // replay the dissolve so a looped intro matches the first one
      this.playCinematicIntro(); // and the cinematic build-up, so the loop opens like the boot
      this.introSequence?.start(() => {
        this.ui.reveal();
        this.ui.setSection(0); // hideSection() ran at the restart grab — bring Chapter 1 back
        if (this.stoneInteraction) this.stoneInteraction.enabled = settings.stones.enabled;
      });
      return;
    }
    // Hand back to scroll just PAST the destination frame (½-frame nudge) so `resolveSection`
    // lands firmly inside the destination chapter rather than a float-hair before its boundary.
    // `force: true` is ESSENTIAL — scroll is still LOCKED, and Lenis silently ignores scrollTo
    // while stopped without it, which left the scroll at the TRIGGER frame → the camera snapped
    // BACKWARD on unlock (the user's "it jumps back" bug, 2026-07-18).
    const destFrac = (p.destFrame + 0.5) / TOTAL_FRAMES;
    const progress = THREE.MathUtils.clamp(this.clipToProgress(destFrac), 0, 1);
    this.scroll.scrollTo(progress * this.scroll.state.limit, { immediate: true, force: true });
    this.scrollCamera.resetSmoothing();
    this.scroll.unlockInput();

    // THE TEXT MOMENT (2026-07-22): camera stopped, settle done, control handed back — then
    // one small BREATH (0.25s) before the words shimmer in. The pause is what makes the
    // arrival read as a composed cut rather than a UI popping on a moving frame.
    this.syncShimmerToScroll();
    this.sectionTextCall?.kill();
    this.sectionTextCall = gsap.delayedCall(0.25, () => {
      this.sectionTextCall = null;
      if (this.currentSectionIndex === p.sectionIndex && !this.magnet.active) {
        this.ui.setSection(p.sectionIndex);
      }
    });
  }

  /** Chapter AMBIENCE for a magnet's destination — scene visibility, music bed, fog, rainbow
   *  gate. Fires at the GRAB so it crossfades during the glide. Deliberately NOT text (that
   *  waits for the stop — finishMagnet) and NOT focal (that waits for `focalFrame` in
   *  updateMagnet). */
  private applyMagnetArrival(p: MagneticPoint): void {
    const section = SECTIONS[p.sectionIndex]!;
    this.currentSectionIndex = p.sectionIndex;
    if (!settings.scenes.loadAllAtOnce) {
      if (settings.scenes.mode === 'exclusive') this.world.showSceneExclusive(section.sceneIndex);
      else this.world.setActiveScene(section.sceneIndex);
    }
    this.sectionState.name = section.name;
    this.sceneAudio.setSection(p.sectionIndex + 1);
    this.applyFogForSection(p.sectionIndex);
  }

  private onTick = (tick: TickState): void => {
    this.scroll.update(tick);

    const introPlaying = this.introSequence?.active ?? false;

    // Same mapping the camera uses: raw scroll 0-1 -> the post-intro clip window.
    const introOffset = this.scrollCamera.introStartFraction;
    const clipFraction = introOffset + this.scroll.state.progress * (1 - introOffset);

    // Scroll velocity in clip-fraction/sec (smoothed lightly) — consumed by startMagnet so the
    // glide takes off at EXACTLY the user's current speed. `prevClip` (last tick's fraction)
    // also feeds the REVERSE magnet crossing detection below.
    const prevClip = this.prevClipFraction;
    if (prevClip !== null && tick.delta > 0) {
      const instVel = (clipFraction - prevClip) / tick.delta;
      this.clipVel += (instVel - this.clipVel) * 0.35;
    }
    this.prevClipFraction = clipFraction;

    // Section transition in flight: hold the scroll where it locked, discarding any input that
    // leaks past `lenis.stop()` (non-cancelable momentum wheel → native scroll). Same defence
    // as the magnets' per-frame pin — without it the camera lurched after each scene flash.
    if (this.sectionTransitioning) {
      this.scroll.scrollTo(this.sectionLockScroll, { immediate: true, force: true });
    }

    // Magnetic transitions own the camera + scroll while active. Only check for a fresh grab
    // when neither the intro nor a magnet is already driving. Trigger detection keys off the
    // CAMERA's fraction (2026-09-01, see `prevCamFraction`): a hard flick jumps RAW scroll past
    // the restart trigger in one tick, which grabbed the restart while the viewer was still in
    // chapter 1 — freezing content/rail through a silent full outro. A magnet now fires only
    // when its trigger frame is actually ON SCREEN.
    const camFraction = this.scrollCamera.lastSampledFraction;
    if (!introPlaying && !this.magnet.active) {
      this.checkMagnetTrigger(camFraction, this.prevCamFraction);
    }
    this.prevCamFraction = camFraction;
    // Normal section boundaries (the C1→C2 flash) only run when nothing else is driving.
    if (!introPlaying && !this.magnet.active) this.updateSection();

    // RAINBOW FRAME GATE (2026-09-03, user: "at frame 170 fade out that rainbow") — driven
    // off the camera's actual frame so it works identically for scroll, glides and scrubs;
    // the setter no-ops when unchanged and the ring's own 2.4s transition does the fading.
    this.cinematic.setRainbowFrameGate(
      camFraction * TOTAL_FRAMES < settings.cinematic.rainbow.fadeOutFrame,
    );

    // SCROLL CUE (2026-09-02, user: "when the user scrolls, that scroll-more thing
    // disappears"): visible only while the viewer actually SITS at the journey's start —
    // any scrolling fades it out (CSS 0.6s), and returning to the top brings it back.
    if (!introPlaying) {
      this.ui.setCueVisible(
        this.currentSectionIndex === 0 &&
          !this.magnet.active &&
          this.scroll.state.progress < 0.008,
      );
    }

    // Frame-triggered one-shots (Scene-1 doors, …) fire off the CAMERA's frame, so a sound
    // lands exactly when its moment is on screen — never seconds early off a flicked scroll.
    this.sceneAudio.checkCues(this.scrollCamera.lastSampledFraction * TOTAL_FRAMES);


    this.scrollReadout?.update(
      this.scroll.state.progress,
      clipFraction,
      this.sectionState.name,
      tick.delta,
    );

    if (introPlaying) this.introSequence!.update(tick); // drives the camera via sampleAt
    else if (this.magnet.active) this.updateMagnet(tick); // magnet drives the camera via sampleAt

    if (this.orbit.enabled) {
      this.orbit.update(tick);
    } else {
      // Scroll drives the camera only when neither the intro nor a magnet is in control.
      if (!introPlaying && !this.magnet.active) {
        this.scrollCamera.update(tick);
        // NO-MAGNETS MODE: the lens is a pure function of the current clip frame — each
        // section's focal is reached by a smoothstep ramp on the approach and holds exactly at
        // the boundary (see `focalAtFrame`), so scrolling forward, scrubbing back, checkpoint
        // landings and the restart all agree with zero snaps. When pulls are ON the glides own
        // the zoom instead (their landing fov matches this function at the landing frame).
        // Skipped during section transitions — the checkpoint flow snaps its own fov under
        // the cloud cover.
        if (!settings.scenes.magneticPulls && !this.sectionTransitioning) {
          const frame = this.scrollCamera.lastSampledFraction * TOTAL_FRAMES;
          this.cameraController.setFovImmediate(
            this.camera.computeFovForFocalLength(
              focalAtFrame(frame, settings.camera.focalRampFrames),
            ),
          );
        }
      }
      this.mouseLook.update(tick); // sets cameraController.parallax before it's consumed below
      this.cameraController.update(tick);
    }

    this.world.update(tick);
    this.interaction.update(tick);
    this.stoneInteraction?.update(tick);
    this.cursorGlow?.update(tick); // AFTER the interaction update — it consumes this tick's hover
    this.sceneAudio.updateListener(this.camera.instance); // spatial audio tracks the camera
    this.cinematic.update(tick.delta); // scintillate the film grain
    // CAMERA FOG ENVELOPE — seat the near-lens cloud sprites on this frame's final camera pose
    // and set their opacity from the current clip frame (covered at 0, clear by 30, re-enveloped
    // 338→352). Runs after every camera mutation this tick (intro/magnet/scroll/teleport), right
    // before the render, so the envelope can never lag the lens by a frame.
    this.world.cameraFog?.update(
      this.camera.instance,
      this.scrollCamera.lastSampledFraction,
      tick.delta,
    );
    // STORY MODE: keep the ring armed for whatever chapter we're actually in (many code paths
    // change the section, so reading it here is more reliable than hooking every one) and advance
    // the hold ring + the drifting smoke field.
    if (this.storyMode) {
      this.storyMode.setChapter(this.currentSectionIndex);
      this.storyMode.update(tick.delta);
      // Ease the voice pulse toward its target (~0.45s swell) and push it to the shaders.
      this.voicePulse += (this.voicePulseTarget - this.voicePulse) * (1 - Math.exp(-2.4 * tick.delta));
      this.world.setStoryPulse(this.voicePulse);
    }
    this.render();
    // Transitions dip FPS on purpose — the adaptive tier must never judge those seconds
    // (a mid-transition tier switch IS the visible flash + quality pop; 2026-07-23).
    this.performance.adaptSuspended =
      introPlaying ||
      this.magnet.active ||
      this.sectionTransitioning ||
      (this.videoTransition?.isPlaying ?? false);
    this.performance.update(tick);
    this.debug?.update(tick);
  };

  private render(): void {
    // Defensive baseline: several things in the pipeline `renderer` state save/restore
    // around their OWN render call — `EffectComposer`'s passes (`RenderPass`,
    // `UnrealBloomPass`) force `renderer.autoClear` false for their multi-pass
    // accumulation, and `WaterComplex`'s `Reflector` similarly saves/restores
    // `renderer.shadowMap.autoUpdate` (+ `xr.enabled`) around its own reflection pass —
    // but every one of those restores only runs on the happy path. If anything about a
    // pass's render call doesn't fully unwind, that state can get stuck corrupted:
    // `autoClear` stuck false means every subsequent frame (composer OR direct) stops
    // clearing the color/depth buffers between draws — new geometry keeps failing
    // depth tests against stale data instead of visibly replacing it, which looks
    // exactly like the scene freezing/breaking (2026-07-05, user: postprocessing
    // checkbox breaking camera movement). Force a known-clean baseline before every
    // frame, regardless of which path is about to run.
    this.renderer.instance.setRenderTarget(null);
    this.renderer.instance.autoClear = true;
    this.renderer.instance.shadowMap.autoUpdate = settings.renderer.shadows.autoUpdate;

    // Reset the draw-call counters ONCE per frame, here, rather than letting three auto-reset
    // them on every `render()` call (`info.autoReset` is disabled in the Renderer ctor). A frame
    // issues several render calls — depth prepass, water reflection, the scene, the bloom pass,
    // the overlay quad — and with auto-reset the perf HUD only ever showed the LAST one: the
    // fullscreen overlay quad, i.e. a permanent, useless "CALLS 1 / TRIS 2" (2026-07-19, user
    // was reading these numbers to diagnose the lag). Resetting per FRAME makes the HUD report
    // the true per-frame totals across every pass.
    this.renderer.instance.info.reset();

    // Capture solid-scene depth for the clouds' soft-particle fade BEFORE the visible render
    // (it binds its own target and restores null; the baseline above still applies to the real
    // frame below). No-op unless `settings.cloudSea.soft.enabled`.
    this.world.renderSceneDepth(this.camera.instance);

    if (this.post.enabled) this.post.render();
    else this.renderer.render(this.sceneManager.scene, this.camera.instance);

    // Additive selective bloom over the direct render (glow without re-tone-mapping the bake).
    // No-ops unless the glow master switch is on — see `setGlowEnabled`.
    if (!this.post.enabled) this.selectiveBloom.render();
  }

  /**
   * THE glow master switch — flips `settings.selectiveBloom.enabled` and pushes it to every site
   * that has to react (bloom-layer opt-in + the additive halo/overlay meshes, which glow on their
   * own without any pass). The debug GUI's "postprocessing" toggle calls this.
   *
   * 2026-07-19, user: "make when only i on post processing than only that emission works, instead
   * it will not work". Note this drives the SELECTIVE bloom, not the full composer — the composer
   * stays off because it re-tone-maps the sky gradient and the baked atlases, which is exactly the
   * problem the user reported when postprocessing was last switched on.
   */
  setGlowEnabled(on: boolean): void {
    settings.selectiveBloom.enabled = on;
    this.world.setGlowEnabled(on);
    this.stoneInteraction?.setGlowEnabled(on);
  }

  /**
   * CINEMATIC BUILD-UP over the 0→40 fly-in (2026-07-20, user: "everything animates like a
   * cinema — clouds animating, clouds revealing, stones appearing, water and everything fills
   * cinematic"). Three synchronized moves, all transform-free (the stones' vertices are baked in
   * world space, so scaling/moving them is off the table — opacity is the safe axis):
   *
   *  - CLOUDS: the whole sea fades up from empty sky.
   *  - STONES: each arch voussoir fades in, staggered around the arch. Every stone briefly wears
   *    a CLONE of its material (the atlas materials are shared — animating the original would
   *    fade all stones at once); the clone is swapped back for the shared original the moment
   *    its fade lands, so the steady state is byte-identical to a cold start.
   *  - WATER: starts choppy and visibly SETTLES into the mirror (distortion + mirror tweened
   *    into the same live settings the GUI drives).
   *
   * Replays on the frame-300 restart loop. Re-entry: the previous timeline is jumped to its end
   * first (progress(1) fires every restore callback), so captured "authored" values are always
   * clean even if the user restarts mid-intro.
   */
  private playCinematicIntro(): void {
    const cfg = settings.intro.cinematic;
    if (!cfg.enabled) return;

    if (this.cinematicTl) {
      this.cinematicTl.progress(1).kill(); // restores originals via the per-stone onCompletes
      this.cinematicTl = null;
    }

    const tl = gsap.timeline();
    this.cinematicTl = tl;

    // 1. Clouds fade up from nothing.
    const clouds = settings.cloudSea;
    const cloudTarget = clouds.opacity;
    clouds.opacity = 0;
    tl.to(clouds, { opacity: cloudTarget, duration: cfg.cloudsSec, ease: 'power2.out' }, 0);

    // 2. Water settles from chop into the mirror.
    const water = settings.water;
    const authored = { distortion: water.distortion, mirror: water.mirror };
    water.distortion = authored.distortion * cfg.waterChop;
    water.mirror = 0;
    tl.to(water, {
      distortion: authored.distortion,
      mirror: authored.mirror,
      duration: cfg.waterSec,
      ease: 'power2.inOut',
    }, 0.4);

    // (The staggered stone fade-in that used to be move 3 was REMOVED 2026-07-21, user:
    // "remove that revealing stone animation, I don't want that right now". The stones are
    // simply present from the first frame; clouds + water still animate. The stoneSec/
    // stoneStagger/stoneDelay settings remain in `settings.intro.cinematic` should it return.)
  }

  private onResize = (sizes: SizesState): void => {
    this.camera.resize(sizes);
    this.cameraController.targetFov = this.camera.instance.fov;
    this.renderer.resize(sizes);
    this.post.resize(sizes);
    this.selectiveBloom.setSize(sizes);
    // Must follow `renderer.resize` — the depth prepass sizes off the renderer's NEW drawing
    // buffer, and its texels must line up with the frame the clouds sample it against.
    this.world.resizeSceneDepth();
    this.scroll.resize();
  };

  dispose(): void {
    this.offTick?.();
    this.offResize?.();
    this.stop();

    this.debug?.dispose();
    this.scrollReadout?.dispose();
    this.preloader.dispose();
    this.ui.dispose();
    this.sceneTransition?.dispose();
    this.videoTransition?.dispose();
    this.stoneInteraction?.dispose();
    this.physics?.dispose();
    this.orbit.dispose();
    this.mouseLook.dispose();
    this.audio.dispose();
    this.stoneAudio.dispose();
    this.sceneAudio.dispose();
    this.cinematic.dispose();
    this.post.dispose();
    this.cursorGlow?.dispose();
    this.storyMode?.dispose();
    this.selectiveBloom.dispose();
    this.performance.dispose();
    this.interaction.dispose();
    this.animation.dispose();
    this.scroll.dispose();
    this.world.dispose();
    this.assets.dispose();
    this.loaders.dispose();
    this.materials.dispose();
    this.renderer.dispose();
    this.time.dispose();
    this.sizes.dispose();
    this.sceneManager.dispose();

    this.log.info('disposed.');
  }
}

/** Smooth acceleration → deceleration for the magnetic camera glide (matches IntroSequence). */
